"use client";

import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import BottomNavigation from "@/src/components/layout/BottomNavigation";
import Header from "@/src/components/layout/header";
import { type IFilter } from "@/src/shared/features/search";
import { useAppRouter } from "@/src/shared/hooks/useAppRouter";
import { formatLocalDate } from "@/src/shared/utils/formateDate";
import clsx from "clsx";
import { isAppliedSmallerSearchBar } from "@/src/shared/utils/global";
import { DEFAULT_GUESTS } from "@/src/shared/constants/defaultValues";
import { useHeaderScroll } from "@/src/shared/hooks/useHeaderScroll";

/** Desktop room for the fixed header with the full search bar; it stays put when the header compacts. */
const DESKTOP_HEADER_SPACE_CLASS = "md:pt-[160px]";

interface ILayoutProps {
  children: React.ReactNode;
  isSearchHide?: boolean;
  DashboardHeader?: React.ReactNode;
  isHideBottomNavigation?: boolean;
}

const Layout: React.FC<ILayoutProps> = ({
  children,
  isSearchHide = false,
  DashboardHeader,
  isHideBottomNavigation,
}) => {
  const router = useAppRouter();
  const headerRef = useRef<HTMLDivElement>(null);

  // State management
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [activeSearchSection, setActiveSearchSection] = useState<
    "where" | "dates" | "guests" | null
  >(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isClient, setIsClient] = useState(false);
  const { headerState, bottomNavVisible } = useHeaderScroll({
    enabled: !isSearchHide && !(isSearchActive && isMobile),
    initialBottomNavVisible: !isHideBottomNavigation,
  });

  const [filters, setFilters] = useState<IFilter>({
    destination: null,
    checkIn: null,
    checkOut: null,
    guests: { ...DEFAULT_GUESTS },
  });

  // Update filters from URL query
  useEffect(() => {
    if (router.isReady) {
      const { query } = router;
      const newFilters: IFilter = {
        destination: null,
        checkIn: null,
        checkOut: null,
        guests: { ...DEFAULT_GUESTS },
      };

      if (query.slug && (query.slug as string[]).includes("nearby")) {
        newFilters.destination = {
          name: "Nearby",
          lat: filters.destination?.lat ?? null,
          lng: filters.destination?.lng ?? null,
          id: 0,
          name_bn: null,
          order: 0,
          tier_1: null,
          tier_2: null,
          within: null,
        };
      } else if (query.address_name) {
        newFilters.destination = {
          name: query.address_name as string,
          lat: filters.destination?.lat ?? null,
          lng: filters.destination?.lng ?? null,
          id: 0,
          name_bn: null,
          order: 0,
          tier_1: null,
          tier_2: null,
          within: null,
        };
      }
      if (query.location && typeof query.location === "string") {
        const [latStr, lngStr] = decodeURIComponent(query.location).split(",");
        const lat = parseFloat(latStr ?? "");
        const lng = parseFloat(lngStr ?? "");

        if (!isNaN(lat) && !isNaN(lng)) {
          newFilters.destination = {
            id: 0,
            name: "",
            name_bn: null,
            order: 0,
            tier_1: null,
            tier_2: null,
            within: null,
            ...newFilters.destination,
            lat,
            lng,
          };
        }
      }

      const hotelId = Number(query.hotel_id);
      if (newFilters.destination && hotelId > 0) {
        newFilters.destination.hotel_id = hotelId;
      }

      if (query.from && typeof query.from === "string") {
        newFilters.checkIn = new Date(query.from);
      }
      if (query.to && typeof query.to === "string") {
        newFilters.checkOut = new Date(query.to);
      }

      if (query.guest && typeof query.guest === "string") {
        newFilters.guests.adults = parseInt(query.guest, 10);
      }
      if (query.child && typeof query.child === "string") {
        newFilters.guests.children = parseInt(query.child, 10);
      }
      if (query.infant && typeof query.infant === "string") {
        newFilters.guests.infants = parseInt(query.infant, 10);
      }
      if (query.rooms && typeof query.rooms === "string") {
        newFilters.guests.rooms = parseInt(query.rooms, 10) || 1;
      }

      setFilters(newFilters);
    }
  }, [router.isReady]);

  // Fix hydration by ensuring client-side rendering
  useEffect(() => {
    setIsClient(true);
  }, []);

  // Check if mobile - only after client-side hydration
  useEffect(() => {
    if (!isClient) return;

    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, [isClient]);

  // An open desktop search closes as soon as the page scrolls.
  useEffect(() => {
    if (isSearchHide || !isSearchActive || isMobile) return;
    const onScroll = () => closeSearch();
    window.addEventListener("scroll", onScroll, { passive: true, once: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [isSearchActive, isMobile, isSearchHide]);

  // Prevent body scroll when search is active on mobile
  useEffect(() => {
    if (isSearchHide) return;
    if (isMobile && isSearchActive) {
      const scrollY = window.scrollY;
      document.body.style.position = "fixed";
      document.body.style.width = "100%";
      document.body.style.top = `-${scrollY}px`;

      return () => {
        document.body.style.position = "";
        document.body.style.width = "";
        document.body.style.top = "";
        window.scrollTo(0, scrollY);
      };
    }
  }, [isMobile, isSearchActive, isSearchHide]);

  // Handle click outside for desktop search
  useEffect(() => {
    if (isSearchHide) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        headerRef.current &&
        !headerRef.current.contains(e.target as Node) &&
        !isMobile &&
        isSearchActive
      ) {
        closeSearch();
      }
    };

    if (isSearchActive && !isMobile) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isMobile, isSearchActive, isSearchHide]);

  /** `next` runs a search the caller has just put together (a recent search). */
  const handleSearch = (next: IFilter = filters) => {
    if (next !== filters) setFilters(next);
    const { destination, checkIn, checkOut, guests } = next;

    if (destination) {
      const isCoxsBazar = (destination.name || "")
        .toLowerCase()
        .includes("cox");

      const query = {
        page: 1,
        limit: 20,
        guest: guests.adults,
        child: guests.children,
        infant: guests.infants,
        location: `${destination.lat},${destination.lng}`,
        from: checkIn ? formatLocalDate(checkIn) : "",
        to: checkOut ? formatLocalDate(checkOut) : "",
        address_name: destination.name,
        ...(destination.within != null && { within: destination.within }),
        ...(destination.tier_1 != null && { tier_1: destination.tier_1 }),
        ...(destination.tier_2 != null && { tier_2: destination.tier_2 }),
        ...(destination.id != null && { location_id: destination.id }),
        ...(destination.hotel_id != null && { hotel_id: destination.hotel_id }),
        ...(isCoxsBazar && { rooms: guests.rooms ?? 1 }),
      };

      const search = new URLSearchParams(
        Object.entries(query).map(([key, value]) => [key, String(value)]),
      );
      void router.push(
        `/s/${destination.name.toLowerCase().replace(/ /g, "-")}?${search.toString()}`,
      );
    } else {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const { latitude, longitude } = position.coords;
            void router.push(
              `/s/nearby?page=1&limit=20&location=${latitude},${longitude}`,
            );
          },
          (error) => {
            console.error("Error getting user location:", error);
            alert(
              "Location access denied. Please enter a destination manually or allow location access in your browser settings.",
            );
          },
        );
      } else {
        alert(
          "Geolocation is not supported by this browser. Please enter a destination manually.",
        );
      }
    }
    closeSearch();
  };

  // Helper functions
  const activateSearch = (section: "where" | "dates" | "guests") => {
    setIsSearchActive(true);
    setActiveSearchSection(section);
  };

  const closeSearch = () => {
    setIsSearchActive(false);
    setActiveSearchSection(null);
  };

  const clearDestination = () =>
    setFilters((f) => ({ ...f, destination: null }));
  const clearDates = () =>
    setFilters((f) => ({ ...f, checkIn: null, checkOut: null }));
  const clearGuests = () =>
    setFilters((f) => ({
      ...f,
      guests: { ...DEFAULT_GUESTS },
    }));

  const updateGuestCount = (
    type: keyof IFilter["guests"],
    op: "add" | "subtract",
  ) => {
    setFilters((f) => {
      const currentRaw = f.guests[type];
      const currentValue =
        typeof currentRaw === "number"
          ? currentRaw
          : type === "rooms" || type === "adults"
            ? 1
            : 0;
      const minValue = type === "adults" || type === "rooms" ? 1 : 0;
      const newValue =
        op === "add" ? currentValue + 1 : Math.max(currentValue - 1, minValue);
      return { ...f, guests: { ...f.guests, [type]: newValue } };
    });
  };

  const getTotalGuests = () => {
    const total = filters.guests.adults + filters.guests.children;
    if (total === 1) return "1 guest";
    if (total === 0) return "Add guests";
    return `${total} guests`;
  };

  // Props to pass to ChatHeading component
  const headerProps = {
    headerRef,
    isSearchActive,
    setIsSearchActive,
    activeSearchSection,
    setActiveSearchSection,
    headerState,
    isMobile,
    bottomNavVisible,
    filters,
    setFilters,
    activateSearch,
    closeSearch,
    clearDestination,
    clearDates,
    clearGuests,
    updateGuestCount,
    getTotalGuests,
    handleSearch,
  };

  const currentPath = router.pathname;
  const isSmallerSearchBar = isAppliedSmallerSearchBar(currentPath);
  // A new page remounts the content; its own query changes don't (a listing's picked
  // dates, the search's filters): the views read the query through hooks and update in
  // place, which keeps their state and scroll. The search page keeps one key across
  // places too, so its filter strip doesn't flash.
  const contentKey = currentPath.startsWith("/s/")
    ? "/s/[...slug]"
    : currentPath;

  if (isSearchHide) {
    return (
      <div className="font-sans bg-generic-white">
        {DashboardHeader ? (
          <div className="w-full">{DashboardHeader}</div>
        ) : null}
        <main className="width-full" key={contentKey}>
          {children}
        </main>
        <BottomNavigation isVisible={bottomNavVisible && !isSearchActive} />
      </div>
    );
  }

  // Normal layout when not hidden and no custom header
  return (
    <div
      // Phone or desktop spacing comes from the breakpoint, not from `isMobile`: that is
      // only known after hydration, so the server HTML had the desktop padding and the
      // whole page jumped up on a phone (a large layout shift).
      className={clsx(
        "font-sans",
        isSmallerSearchBar && !isSearchActive
          ? "bg-generic-white"
          : "bg-secondary",
        "max-md:!bg-generic-white max-md:pt-[35px]",
        isSmallerSearchBar ? "md:pt-[60px]" : DESKTOP_HEADER_SPACE_CLASS,
        bottomNavVisible && !isSearchActive ? "max-md:pb-20" : "max-md:pb-0",
        "md:pb-0",
      )}
    >
      {/* ChatHeading */}
      <header
        ref={headerRef}
        data-app-header="true"
        className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 max-md:h-15 ${isSmallerSearchBar ? " pb-4 border-b border-gray-100 bg-generic-white max-md:h-0" : "bg-secondary"}`}
      >
        <div
          className={`w-full mx-auto ${isSmallerSearchBar && !isSearchActive ? "bg-generic-white" : "bg-secondary max-md:bg-generic-white"}`}
        >
          <Header {...headerProps} />
        </div>
      </header>

      {/* Desktop search overlay: transparent like Airbnb's (no dimming); it only
          catches the outside click that closes the search. */}
      <>
        {isSearchActive && !isMobile && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40"
            onClick={closeSearch}
          />
        )}
      </>

      <main className="width-full" key={contentKey}>
        {children}
      </main>

      {/* Bottom Navigation - Hidden when search is active */}
      <BottomNavigation isVisible={bottomNavVisible && !isSearchActive} />
    </div>
  );
};

export default Layout;
